import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { inr, fmtDate } from "@/utils";
import { ArrowDownRight, ArrowUpRight, Truck } from "lucide-react";

const TYPE_STYLES = {
  purchase: { icon: ArrowDownRight, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Purchase" },
  issue: { icon: ArrowUpRight, cls: "bg-orange-50 text-orange-700 border-orange-200", label: "Issue" },
  direct_site: { icon: Truck, cls: "bg-purple-50 text-purple-700 border-purple-200", label: "Direct Site" },
  return: { icon: ArrowDownRight, cls: "bg-blue-50 text-blue-700 border-blue-200", label: "Return" },
  adjust: { icon: ArrowDownRight, cls: "bg-zinc-100 text-zinc-700 border-zinc-200", label: "Adjust" },
};

export default function StockMovements() {
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const [projectId, setProjectId] = useState("");
  const [type, setType] = useState("");

  const { data: movements = [] } = useQuery({
    queryKey: ["stock-movements", projectId, type],
    queryFn: async () => (await api.get("/stock-movements", { params: { project_id: projectId || undefined, mtype: type || undefined } })).data,
  });

  return (
    <div className="space-y-6" data-testid="stock-movements-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Construction</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Stock Movement Register</h1>
          <p className="text-sm text-zinc-500 mt-1">Full trail: every purchase, issue, direct-to-site, return and adjustment.</p>
        </div>
        <div className="flex gap-2">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-64" data-testid="sm-project"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-40" data-testid="sm-type"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All types</SelectItem>
              <SelectItem value="purchase">Purchase (in)</SelectItem>
              <SelectItem value="issue">Issue (out)</SelectItem>
              <SelectItem value="direct_site">Direct Site</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="bg-white border-zinc-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-zinc-50"><TableRow>
              <TableHead>Date/Time</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Material</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Actor</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {movements.map((m) => {
                const t = TYPE_STYLES[m.type] || TYPE_STYLES.adjust;
                return (
                  <TableRow key={m.id} className="data-row" data-testid={`movement-row-${m.id}`}>
                    <TableCell className="text-xs font-mono">{fmtDate(m.at)} · {m.at?.slice(11, 16)}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${t.cls}`}>
                        <t.icon size={11} /> {t.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{m.material_name}</TableCell>
                    <TableCell className={`text-right font-mono ${m.direction === "out" ? "text-red-600" : "text-emerald-700"}`}>
                      {m.direction === "out" ? "-" : "+"}{m.qty}
                    </TableCell>
                    <TableCell className="text-right font-mono">{inr(m.rate)}</TableCell>
                    <TableCell className="text-right font-mono">{inr(m.amount)}</TableCell>
                    <TableCell className="text-xs">{m.ref_type} · {m.ref_id?.slice(0, 8)}</TableCell>
                    <TableCell className="text-xs">{m.actor_name}</TableCell>
                  </TableRow>
                );
              })}
              {!movements.length && <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-500 py-16">No stock movements yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
